import Vue from 'vue'
import Vuex from 'vuex'
import { setAuthedFlag, clearAuthLocal } from '@/utils/auth'
import { defaultUserProfile } from '@/utils/user-profile'
import { defaultSiteConfig, setSiteConfig } from '@/utils/site'

Vue.use(Vuex)

function normalizeUser(payload) {
  if (!payload) return null
  const features = Object.assign(
    {
      aiBeautify: false,
      aiBeautifyVisible: false,
      aiBeautifyShowAll: false,
      aiBeautifyMaxPerQr: 0,
      aiBeautifyDenyMessage: '',
      aiBeautifyReason: ''
    },
    (payload.features && typeof payload.features === 'object') ? payload.features : {}
  )
  const canUse = !!features.aiBeautify
  const visible = !!features.aiBeautifyVisible || canUse
  return {
    id: payload.id,
    user: payload.user || payload.username || '',
    username: payload.username || payload.user || '',
    email: payload.email || '',
    phone: payload.phone || '',
    hydj: payload.hydj,
    kjdx: payload.kjdx,
    kjzls: payload.kjzls,
    banned: !!payload.banned,
    status: payload.status || '',
    statusLabel: payload.statusLabel || '',
    isDeleted: !!payload.isDeleted,
    registeredAt: payload.registeredAt || '',
    member: Object.assign(
      {},
      defaultUserProfile.member,
      payload.member || {}
    ),
    features: {
      aiBeautify: canUse,
      aiBeautifyVisible: visible,
      aiBeautifyShowAll: !!features.aiBeautifyShowAll,
      aiBeautifyMaxPerQr: Math.max(0, parseInt(features.aiBeautifyMaxPerQr, 10) || 0),
      aiBeautifyDenyMessage: features.aiBeautifyDenyMessage ? String(features.aiBeautifyDenyMessage) : '',
      aiBeautifyReason: features.aiBeautifyReason ? String(features.aiBeautifyReason) : ''
    }
  }
}

export default new Vuex.Store({
  state: {
    isCollapse: false,
    unreadMessageCount: 0,
    ticketOpenCount: 0,
    user: null,
    userLoaded: false,
    siteConfig: { ...defaultSiteConfig },
    siteLoaded: false
  },
  getters: {
    unreadMessageCount(state) {
      return state.unreadMessageCount
    },
    ticketOpenCount(state) {
      return state.ticketOpenCount
    },
    currentUser(state) {
      return state.user
    },
    siteConfig(state) {
      return state.siteConfig
    },
    userProfileView(state) {
      if (!state.user) {
        return defaultUserProfile
      }
      return {
        username: state.user.username || state.user.user || '用户',
        email: state.user.email || '',
        member: state.user.member || defaultUserProfile.member,
        features: state.user.features || defaultUserProfile.features
      }
    }
  },
  mutations: {
    toggleCollapse(state) {
      state.isCollapse = !state.isCollapse
    },
    setCollapse(state, value) {
      state.isCollapse = value
    },
    setUser(state, user) {
      state.user = normalizeUser(user)
      state.userLoaded = true
    },
    clearUser(state) {
      state.user = null
      state.userLoaded = false
      state.unreadMessageCount = 0
      state.ticketOpenCount = 0
    },
    setSiteConfig(state, cfg) {
      state.siteConfig = setSiteConfig(cfg)
      state.siteLoaded = true
    },
    setUnreadMessageCount(state, count) {
      state.unreadMessageCount = Math.max(0, Number(count) || 0)
    },
    setTicketOpenCount(state, count) {
      state.ticketOpenCount = Math.max(0, Number(count) || 0)
    }
  },
  actions: {
    async loadSiteConfig({ commit, state }, { force = false } = {}) {
      if (state.siteLoaded && !force) {
        return state.siteConfig
      }
      try {
        const { fetchSiteConfig } = require('@/api/site')
        const res = await fetchSiteConfig()
        commit('setSiteConfig', res.data || {})
      } catch (e) {
        commit('setSiteConfig', defaultSiteConfig)
      }
      return state.siteConfig
    },
    async loadCurrentUser({ commit, state, dispatch }, { force = false } = {}) {
      if (state.userLoaded && state.user && !force) {
        return state.user
      }
      const { fetchMe } = require('@/api/auth')
      const res = await fetchMe()
      commit('setUser', res.data)
      setAuthedFlag(true)
      if (!(res.data && res.data.banned)) {
        dispatch('refreshBadges')
      }
      return res.data
    },
    async refreshBadges({ commit }) {
      try {
        const { fetchMessageUnread } = require('@/api/message')
        const msgRes = await fetchMessageUnread()
        commit('setUnreadMessageCount', (msgRes.data && msgRes.data.unread) || 0)
      } catch (e) {
        // ignore
      }
      try {
        const { fetchTicketList } = require('@/api/ticket')
        const tkRes = await fetchTicketList({ page: 1, pageSize: 1 })
        commit('setTicketOpenCount', (tkRes.data && tkRes.data.openCount) || 0)
      } catch (e) {
        // ignore
      }
    },
    async logout({ commit }) {
      try {
        const { logout: logoutApi } = require('@/api/auth')
        await logoutApi()
      } catch (e) {
        // 即使接口失败也清理本地会话
      }
      clearAuthLocal()
      commit('clearUser')
    }
  }
})
